import axios from "axios";

import { API_ORIGIN } from "./apiConfig";

const API_BASE_URL = `${API_ORIGIN}/usp`;

export interface JobPayload {
    title: string;
    department: string;
    subThematicArea: string;
    assignmentType: string;
    subAssignmentType: string;
    location: string;
    type: string;
    description: string;
    education: string;
    qualifications: string[];
    responsibilities: string[];
    status: string;
}

const getAuthHeaders = () => {
    const token = localStorage.getItem("admin_access_token");
    return {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
};

// ==============================
// ✅ ADMIN JOB MANAGEMENT
// ==============================

// Create Job
export const createJob = async (data: JobPayload) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/admin/jobs`, data, {
            headers: getAuthHeaders(),
        });
        return response.data;
    } catch (error: any) {
        console.error("Error creating job:", error);
        throw error.response?.data || { message: "Something went wrong" };
    }
};

// Get All Jobs (Admin)
export const getJobsAdmin = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/admin/jobs`, {
            headers: getAuthHeaders(),
        });
        return response.data;
    } catch (error: any) {
        console.error("Error fetching jobs:", error);
        throw error.response?.data || { message: "Failed to fetch jobs" };
    }
};

// Delete Job
export const deleteJob = async (id: string) => {
    try {
        const response = await axios.delete(`${API_BASE_URL}/admin/jobs/${id}`, {
            headers: getAuthHeaders(),
        });
        return response.data;
    } catch (error: any) {
        console.error("Error deleting job:", error);
        throw error.response?.data || { message: "Failed to delete job" };
    }
};

// Get Job by ID (Admin)
export const getAdminJobById = async (id: string) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/admin/jobs/${id}`, {
            headers: getAuthHeaders(),
        });
        return response.data;
    } catch (error: any) {
        console.error(`Error fetching admin job ${id}:`, error);
        throw error.response?.data || { message: "Failed to fetch job details" };
    }
};

// Update Job
export const updateJob = async (id: string, data: JobPayload) => {
    try {
        const response = await axios.put(`${API_BASE_URL}/admin/jobs/${id}`, data, {
            headers: getAuthHeaders(),
        });
        return response.data;
    } catch (error: any) {
        console.error(`Error updating job ${id}:`, error);
        throw error.response?.data || { message: "Failed to update job" };
    }
};

// ==============================
// ✅ PUBLIC JOB ENDPOINTS
// ==============================

// Get All Public Jobs
export const getJobs = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/jobs`);
        return response.data;
    } catch (error: any) {
        console.error("Error fetching jobs:", error);
        throw error.response?.data || { message: "Failed to fetch jobs" };
    }
};

// Get Job Details by ID (Public)
export const getJobDetails = async (id: number) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/jobs/${id}`);
        return response.data;
    } catch (error: any) {
        console.error("Error fetching job details:", error);
        throw error.response?.data || { message: "Failed to fetch job details" };
    }
};

// Search Jobs
export const searchJobs = async (keyword: string) => {
    try {
        const response = await axios.get(
            `${API_BASE_URL}/jobs/search?keyword=${encodeURIComponent(keyword)}`
        );
        return response.data;
    } catch (error: any) {
        console.error("Error searching jobs:", error);
        throw error.response?.data || { message: "Failed to search jobs" };
    }
};

// ==============================
// ✅ APPLY JOB (PUBLIC FORM)
// ==============================
export const applyJob = async (id: number, data: FormData) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/jobs/${id}/apply`, data, {
            headers: { "Content-Type": "application/json" },
        });
        return response.data;
    } catch (error: any) {
        console.error("Error applying for job:", error);
        throw error.response?.data || { message: "Failed to apply for job" };
    }
};

// ==============================
// ✅ MEDIA SERVICE (NO TOKEN)
// ==============================
export const jobMediaService = {
    // Upload Job Media
    uploadMedia: async (files: any[], additionalData: any = {}) => {
        try {
            const formData = new FormData();

            // Append each file
            files.forEach((fileObj) => {
                formData.append("files", fileObj.file);
            });

            // Append additional fields if any
            Object.keys(additionalData).forEach((key) => {
                formData.append(key, additionalData[key]);
            });

            const response = await axios.post(
                `${API_ORIGIN}/api/assets/upload`,
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
            );

            return response.data;
        } catch (error: any) {
            if (error.response) {
                throw new Error(
                    `API Error: ${error.response.status} - ${error.response.data?.message || "Unknown error"
                    }`
                );
            } else if (error.request) {
                throw new Error("Network error: No response from server");
            } else {
                throw new Error(`Request error: ${error.message}`);
            }
        }
    },

    // Delete Job Media
    deleteMedia: async (fileName: string) => {
        try {
            const response = await axios.delete(
                `${API_ORIGIN}/api/assets/delete/${fileName}`
            );
            return response.data;
        } catch (error: any) {
            if (error.response) {
                throw new Error(
                    `API Error: ${error.response.status} - ${error.response.data?.message || "Unknown error"
                    }`
                );
            } else if (error.request) {
                throw new Error("Network error: No response from server");
            } else {
                throw new Error(`Request error: ${error.message}`);
            }
        }
    },
};
